import { useState } from 'react'
import type {
  ManifestRuleApproval,
  ManifestRuleWithStatus,
} from '../../api/ws'

// Install wizard: lets an operator review a plugin manifest's
// default_rules and approve the subset they want minted. Used both
// post-connection-create (org-scoped rules) and pre-agent-grant
// (agent-scoped rules); the caller wires the submit handler to the
// appropriate approve endpoint.
//
// UX intent: simple-case fast path is "every rule pre-checked, hit
// Approve, done." The toggle + decision dropdown exist for when the
// operator wants to be selective or override the manifest's
// suggestion. See docs/iam-rule-types.md §"Plugin manifest
// default_rules are suggestions, not auto-grants" and
// docs/approver-resolution.md (this is the first major surface that
// consumes `decision='ask'`).

export function InstallWizard({
  manifestDefaults,
  scopeLabel,
  submitLabel,
  onApprove,
  onCancel,
  busy,
  mode = 'install',
}: {
  manifestDefaults: ManifestRuleWithStatus[]
  // Short label rendered in the header — "for household" /
  // "for agent <name>" — so operators know what scope they're
  // approving for. Optional.
  scopeLabel?: string
  submitLabel?: string
  // Called with the operator-edited subset. Caller routes to the
  // appropriate approve endpoint.
  onApprove: (rules: ManifestRuleApproval[]) => Promise<void> | void
  onCancel?: () => void
  busy?: boolean
  // 'install' (default): post-create / pre-grant flow. Already-minted
  //   rules default to unselected ("don't re-prompt me"), un-minted
  //   default to selected ("approve the new things").
  // 'edit': fine-grained editor for an existing grant. Already-minted
  //   rules default to SELECTED so flipping their decision dropdown
  //   writes the change on submit. Un-minted still default to selected
  //   so the operator can add manifest verbs that weren't part of the
  //   original approval.
  // Submit is the same UPSERT either way; the difference is just which
  // rows default to "include in submit."
  mode?: 'install' | 'edit'
}) {
  // Local state: each manifest rule starts checked per the mode rules
  // above. Decision starts at the manifest's suggestion unless there's
  // an existing minted decision, in which case use that.
  const [picks, setPicks] = useState<
    { action: string; decision: 'allow' | 'deny' | 'ask'; selected: boolean }[]
  >(() =>
    manifestDefaults.map((r) => ({
      action: r.action,
      decision: (r.minted_decision ?? r.decision) as 'allow' | 'deny' | 'ask',
      selected: mode === 'edit' ? true : !r.already_minted,
    })),
  )

  function setPick(
    idx: number,
    patch: Partial<{ decision: 'allow' | 'deny' | 'ask'; selected: boolean }>,
  ) {
    setPicks((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)))
  }

  function setAllSelected(v: boolean) {
    setPicks((prev) => prev.map((p) => ({ ...p, selected: v })))
  }

  async function submit() {
    const approved: ManifestRuleApproval[] = picks
      .filter((p) => p.selected)
      .map((p) => ({ action: p.action, decision: p.decision }))
    await onApprove(approved)
  }

  if (manifestDefaults.length === 0) {
    return (
      <div style={{ padding: '12px 16px', fontSize: 14 }}>
        <p style={{ opacity: 0.65, marginTop: 0 }}>
          This plugin's manifest doesn't suggest any default rules.
          Nothing to approve — agents granted access to this connection
          will need rules added by hand via the rules editor.
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {onCancel && (
            <button type="button" onClick={onCancel}>
              Close
            </button>
          )}
        </div>
      </div>
    )
  }

  const allSelected = picks.every((p) => p.selected)
  const noneSelected = picks.every((p) => !p.selected)

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      <p style={{ opacity: 0.65, marginTop: 0 }}>
        {mode === 'edit' ? (
          <>
            Edit which verbs this agent can use
            {scopeLabel ? ' ' + scopeLabel : ''}. Change a decision
            (<code>allow</code> / <code>ask</code> / <code>deny</code>)
            to override the current setting; un-check a verb to leave
            its current rule untouched. To fully remove a verb from
            the grant, revoke the whole grant or use the Rules debug
            sheet.
          </>
        ) : (
          <>
            The plugin suggests these default rules
            {scopeLabel ? ' ' + scopeLabel : ''}. Approve the subset
            you want; un-check anything you don't, and override the
            suggested decision if you'd rather <code>allow</code>{' '}
            instead of <code>ask</code> (or vice versa). Plaintext
            predicates from the manifest are not editable here.
          </>
        )}
      </p>

      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          onClick={() => setAllSelected(!allSelected)}
          disabled={busy}
        >
          {allSelected ? 'Un-check all' : 'Check all'}
        </button>
        <span style={{ opacity: 0.6, fontSize: 12 }}>
          {picks.filter((p) => p.selected).length} of {picks.length}{' '}
          selected
        </span>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {manifestDefaults.map((rule, i) => {
          const pick = picks[i]
          const overridden =
            !rule.already_minted && pick.decision !== rule.decision
          return (
            <li
              key={rule.action}
              style={{
                padding: '10px 0',
                borderBottom: '1px solid #eee',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={pick.selected}
                  onChange={(e) => setPick(i, { selected: e.target.checked })}
                  disabled={busy}
                  style={{ flexShrink: 0 }}
                />
                <code style={{ fontFamily: 'monospace', flex: 1, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
                  {rule.action}
                </code>
                <select
                  value={pick.decision}
                  onChange={(e) =>
                    setPick(i, {
                      decision: e.target.value as 'allow' | 'deny' | 'ask',
                    })
                  }
                  disabled={busy || !pick.selected}
                  style={{ fontSize: 16, flexShrink: 0 }}
                >
                  <option value="allow">allow</option>
                  <option value="ask">ask</option>
                  <option value="deny">deny</option>
                </select>
              </div>
              <div style={{ paddingLeft: 24, fontSize: 12, opacity: 0.7 }}>
                {rule.already_minted && (
                  <span
                    style={{
                      color: '#060',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginRight: 8,
                    }}
                  >
                    already approved
                  </span>
                )}
                {overridden && (
                  <span
                    style={{
                      color: '#960',
                      marginRight: 8,
                    }}
                  >
                    overriding manifest's <code>{rule.decision}</code>
                  </span>
                )}
                {rule.when && Object.keys(rule.when).length > 0 && (
                  <span style={{ opacity: 0.6 }}>
                    when {JSON.stringify(rule.when)}
                  </span>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          onClick={submit}
          disabled={busy || noneSelected}
          style={{ padding: '8px 12px' }}
        >
          {busy ? 'Approving…' : (submitLabel ?? 'Approve selected')}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}
